import { Type } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { getHighestPriorityItem, toAlertPriority } from '@pncl/common-models';
import { URL_CONSTANTS } from 'src/app/common/utils/url';
import { findPriorityConfig } from 'src/app/utilities/alert.utils';
import {
  Alert,
  AlertPriority,
  PriorityConfig,
  SafeguardingInsightsAlertEntity,
} from '../../models/alert';
import { RelatedSpaceComponent } from '../alert-details/related-space/related-space.component';
import { AlertPrimaryAction } from './alert.view-model';
import { SpaceAlertViewModel } from './space-alert.view-model';

export class SafeguardingAlertViewModel extends SpaceAlertViewModel<SafeguardingInsightsAlertEntity> {
  constructor(
    alert: Alert<SafeguardingInsightsAlertEntity>,
    translateService: TranslateService,
    private readonly priorityConfigs: PriorityConfig[],
    priorityConfig: PriorityConfig | null = null,
  ) {
    super(alert, translateService, priorityConfig);
  }

  get title(): string {
    const priority = this.priorityConfig?.label ?? '';
    if (this.isSpaceDeleted) {
      return this.translateService.instant('{{priority}} safeguarding issue', { priority });
    }

    return this.translateService.instant('{{priority}} safeguarding issue: {{spaceName}}', {
      priority,
      spaceName: this.spaceName,
    });
  }

  override get description(): string {
    const items = this.entity?.items ?? [];
    const highestPriority = toAlertPriority(getHighestPriorityItem(items)?.itemSeverity);
    const highestPriorityCount = items.filter(
      (item) => toAlertPriority(item.itemSeverity) === highestPriority,
    ).length;
    const otherIssueCount = items.length - highestPriorityCount;

    const found = this.foundClause(highestPriorityCount, highestPriority);
    if (otherIssueCount < 1) {
      return found;
    }

    return `${found}, ${this.otherClause(otherIssueCount)}`;
  }

  override get relatedComponentType(): Type<unknown> | null {
    return RelatedSpaceComponent;
  }

  override get alertTypeLabel(): string {
    return this.translateService.instant('Safeguarding');
  }

  override get primaryAction(): AlertPrimaryAction | null {
    return {
      label: this.translateService.instant('View issue'),
      icon: 'open_in_new',
      execute: () => window.open(`/${URL_CONSTANTS.RECORDINGS}`, '_blank'),
    };
  }

  /** Named after the items' own severity, not the re-triageable `alert.priority`. */
  private foundClause(count: number, priority: AlertPriority | undefined): string {
    const priorityName = findPriorityConfig(this.priorityConfigs, priority)?.label.toLowerCase();

    // No severity mapped — every `itemSeverity` was unrecognised, which
    // `getHighestPriorityItem` reports through `onUnrecognisedSeverity`.
    // Interpolating undefined would leave the literal `{{priorityName}}` on screen.
    if (!priorityName) {
      return this.translateService.instant(
        count === 1
          ? '{{count}} issue was found in this session'
          : '{{count}} issues were found in this session',
        { count },
      );
    }

    return this.translateService.instant(
      count === 1
        ? '{{count}} {{priorityName}} issue was found in this session'
        : '{{count}} {{priorityName}} issues were found in this session',
      { count, priorityName },
    );
  }

  private otherClause(otherCount: number): string {
    return this.translateService.instant(
      otherCount === 1
        ? '{{otherCount}} other safeguarding issue was also reported'
        : '{{otherCount}} other safeguarding issues were also reported',
      { otherCount },
    );
  }
}
